import {useState, useMemo, useEffect, useCallback} from 'react'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import './FlowList.scss'
import {toast} from 'react-toastify'
import ActionMenuPortal from '../../contact-collection/components/ActionMenuPortal'
import {
  useDeleteFlowMutation,
  useGetFlowsQuery,
  useUpdateFlowStatusMutation,
  useGetInstanceLogsStatusQuery,
  useGetFlowStatsQuery,
  useCreateFlowMutation,
  useUpdateFlowSettingsMutation,
} from '../../../../../services/cellcast.v2'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import ResumeFlowModal from '../Logs/Components/ResumeFlowModal'
import {
  DEFAULT_PAGINATION,
  PAGINATION_CLASSES,
  PAGINATION_OPTIONS,
} from '../../../../Utils/Constant/defaultPagination'
import {formatDate} from '../../../../Utils/Constant/formatDayTime'
import {RoutePaths} from '../../../../routing/RoutePath'
import {useNavigate, useLocation} from 'react-router-dom'
import {Flow, FlowBusinessHoursSetting, FlowStatus, IFlowStatisticsSummary} from '../WorkFlow/FlowTypes'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {useSelector} from 'react-redux'
import {getAllUserDetails} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {useResponsive} from '../../../../hooks/useResponsive'
import {FlowMobileCard} from './FlowMobileCard'
import {FlowRowProps} from './types'
import {ADMIN_ROLES, ROUTES} from './constants'
import {useDuplicateFlow} from './hooks/useDuplicateFlow'
import {buildMenuItems} from './utils/menuItemsBuilder'
import FlowSettingsModal, {FlowSettingItem} from './FlowSettingsModal'

const FlowRow = ({
  data,
  idx,
  navigate,
  handleNavigateToInstanceLogs,
  handleNavigateToReport,
  setSelectedId,
  setOpen,
  isUpdating,
  updatingId,
  handleToggleStatus,
  t,
  formatDate,
  FlowStatus,
  isAdmin,
  setResumeModalOpen,
  setResumeFlowId,
  setResumeFlowName,
  handleDuplicateFlow,
  setPortalMenuAnchor,
  setPortalFlow,
}: FlowRowProps) => {
  const {data: statusData, isLoading: isStatusLoading} = useGetInstanceLogsStatusQuery({
    id: data._id,
  })

  const statusBreakdown = (statusData?.data?.statistics as IFlowStatisticsSummary)?.statusBreakdown
  return (
    <tr
      key={idx}
      className='hover-row cursor-pointer'
      onClick={() => {
        if (!isAdmin) {
          navigate(ROUTES.CONTACTS_IN_FLOWS(data._id), {state: {flow: data}})
        }
      }}
    >
      <td>
        <div className='d-flex flex-column'>
          <span className='text-dark fw-bolder d-block mb-1 fs-6'>{data?.name}</span>
        </div>
      </td>
      <td>
        <div className='d-flex align-items-center gap-2'>
          {data?.status === FlowStatus.ACTIVE ? (
            <i className='bi bi-play-circle text-success'></i>
          ) : (
            <i className='bi bi-stop-circle text-muted'></i>
          )}
          <span className='text-dark fw-400 d-block mb-1 fs-6'>
            {data?.status === FlowStatus.ACTIVE ? 'Active' : 'Inactive'}
          </span>
        </div>
      </td>
      <td>
        <span className='text-dark fw-400 d-block mb-1 fs-6'>{statusBreakdown?.bites || '-'}</span>
      </td>
      <td>
        {isStatusLoading ? (
          <Spinner size='sm' />
        ) : (
          <div className='d-flex flex-column'>
            <span className='text-muted fs-8'>Started: {statusBreakdown?.started ?? '-'}</span>
            <span className='text-muted fs-8'>In Progress: {statusBreakdown?.running ?? '-'}</span>
            <span className='text-muted fs-8'>Completed: {statusBreakdown?.completed ?? '-'}</span>
            <span className='text-muted fs-8'>Paused: {statusBreakdown?.paused ?? '-'}</span>
          </div>
        )}
      </td>
      <td>
        <span className='text-dark fw-400 d-block mb-1 fs-6'>
          {data?.haveJobPaused ? (
            <div className='d-flex align-items-center'>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{'Flow paused due to insufficient credits.'}</Tooltip>}
              >
                <i className='bi bi-info-circle me-2 text-danger'></i>
              </OverlayTrigger>
              <h5 className='text-danger me-1'>Flow paused, requires attention</h5>
            </div>
          ) : (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{'Working fines. No more actions needed.'}</Tooltip>}
            >
              <i className='bi bi-check-circle me-2 text-success'></i>
            </OverlayTrigger>
          )}
        </span>
      </td>
      <td>
        <span className='text-dark fw-400 d-block mb-1 fs-6'>{formatDate(data?.createdAt)}</span>
      </td>
      <td>
        <span className='text-dark fw-400 d-block mb-1 fs-6'>
          {data?.updatedAt ? formatDate(data.updatedAt) : '-'}
        </span>
      </td>
      <td className='ms-2 text-end d-flex justify-content-end'>
        <button
          className='btn btn-light-primary btn-sm me-3'
          onClick={(e) => {
            e.stopPropagation()
            if (!isAdmin) {
              handleNavigateToReport(data?._id)
            }
          }}
          title='View Report'
        >
          <i className='bi bi-bar-chart me-2'></i>
          View Report
        </button>
        <span
          className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary ms-3 btn-action-table flow-more-action-icon-btn'
          title='More Actions'
          onClick={(e) => {
            e.stopPropagation()
            setPortalMenuAnchor(e.currentTarget as HTMLElement)
            setPortalFlow(data)
          }}
          onMouseEnter={(e) => {
            setPortalMenuAnchor(e.currentTarget as HTMLElement)
            setPortalFlow(data)
          }}
        >
          <span className='flow-more-action-icon'>...</span>
        </span>
      </td>
    </tr>
  )
}

const FlowList = () => {
  const {t} = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const {isMobile} = useResponsive()

  const userDetails = useSelector(getAllUserDetails)
  const userRole = userDetails?.role
  const isAdmin = ADMIN_ROLES.includes(userRole as (typeof ADMIN_ROLES)[number])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION.limit)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [resumeModalOpen, setResumeModalOpen] = useState(false)
  const [resumeFlowId, setResumeFlowId] = useState<string>('')
  const [resumeFlowName, setResumeFlowName] = useState<string>('')
  const [portalMenuAnchor, setPortalMenuAnchor] = useState<HTMLElement | null>(null)
  const [portalFlow, setPortalFlow] = useState<Flow | null>(null)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [settingsFlow, setSettingsFlow] = useState<Flow | null>(null)
  const [stopOnResponse, setStopOnResponse] = useState(false)
  const [allowMultipleBitesNotifications, setAllowMultipleBitesNotifications] = useState(false)
  const [businessHours, setBusinessHours] = useState<FlowBusinessHoursSetting | undefined>(undefined)
  const [updateFlowSettings, {isLoading: isSavingSettings}] = useUpdateFlowSettingsMutation()

  const {data: listFlow, isLoading, error, refetch} = useGetFlowsQuery({page, limit: pageSize})
  const [deleteFlow] = useDeleteFlowMutation()
  const [updateFlowStatus, {isLoading: isUpdating}] = useUpdateFlowStatusMutation()
  const [createFlow] = useCreateFlowMutation()
  const flows = useMemo(() => listFlow?.data?.items || [], [listFlow])
  const totalPages = useMemo(() => listFlow?.data?.totalPages || 1, [listFlow])

  // Fetch overall flow statistics for header stats bar
  const {
    data: flowStatsResp,
    isLoading: isFlowStatsLoading,
    refetch: refetchFlowStats,
  } = useGetFlowStatsQuery()
  const flowStats = flowStatsResp?.data

  const handleCreateNewFlow = useCallback(() => {
    isAdmin ? navigate(RoutePaths.templateAutomationNew) : navigate(RoutePaths.automationNew)
  }, [navigate, isAdmin])

  const handleViewLogsPage = useCallback(() => {
    navigate(RoutePaths.automationLogs)
  }, [navigate])

  const handleNavigateToInstanceLogs = useCallback(
    (id: string) => {
      const data = flows.find((flow) => flow._id === id)
      if (data) {
        navigate(ROUTES.CONTACTS_IN_FLOWS(data._id), {state: {flow: data}})
      }
    },
    [flows, navigate]
  )

  const handleNavigateToReport = useCallback(
    (id: string) => {
      navigate(ROUTES.FLOW_REPORT(id))
    },
    [navigate]
  )

  const handleDeleteConfirm = useMemo(() => {
    return async () => {
      if (selectedId) {
        try {
          await deleteFlow({id: selectedId}).unwrap()
          toast.success(t('FLOWLIST.DELETE_SUCCESS'))
          refetch()
        } catch (error) {
          toast.error(t('FLOWLIST.DELETE_FAILER'))
        } finally {
          setOpen(false)
          setSelectedId(null)
        }
      }
    }
  }, [selectedId, deleteFlow, t, refetch])

  const handleToggleStatus = async (id: string, status: FlowStatus) => {
    setUpdatingId(id)
    try {
      const res = await updateFlowStatus({id, status})
      if ('data' in res) {
        toast.success(res.data.message)
        refetch()
        refetchFlowStats()
      } else if ('error' in res) {
        const errorMessage =
          (
            res.error as {
              data: {
                message: string
              }
            }
          )?.data?.message || t('FLOWLIST.UPDATE_STATUS_ERROR')
        toast.error(errorMessage)
      }
    } catch (error) {
      toast.error(t('FLOWLIST.UPDATE_STATUS_ERROR'))
    } finally {
      setUpdatingId(null)
    }
  }

  const handleResumeSuccess = useCallback(() => {
    refetch()
    refetchFlowStats()
  }, [refetch, refetchFlowStats])

  const handleOpenSettings = useCallback((flow: Flow) => {
    setSettingsFlow(flow)
    setStopOnResponse(Boolean(flow.settings?.isStopOnReply))
    setAllowMultipleBitesNotifications(Boolean(flow.settings?.allowMultipleBitesNotifications))

    const currentBh = flow.settings?.businessHours
    if (currentBh) {
      setBusinessHours({
        status: Boolean(currentBh.status),
        startTime: currentBh.startTime || '09:00',
        endTime: currentBh.endTime || '18:00',
        days:
          Array.isArray(currentBh.days) && currentBh.days.length > 0 ? currentBh.days : [1, 2, 3, 4, 5],
        timezone: currentBh.timezone || 'Australia/Melbourne',
      })
    } else {
      setBusinessHours(undefined)
    }

    setSettingsModalOpen(true)
  }, [])

  const handleSaveSettings = useCallback(async () => {
    if (!settingsFlow) return
    try {
      const settingsPayload: Record<string, unknown> = {
        isStopOnReply: stopOnResponse,
        allowMultipleBitesNotifications,
      }

      if (businessHours) {
        settingsPayload.businessHours = businessHours
      }

      await updateFlowSettings({
        id: settingsFlow._id,
        settings: settingsPayload,
      }).unwrap()
      toast.success('Settings saved')
      refetch()
      refetchFlowStats()
      setSettingsModalOpen(false)
    } catch (error) {
      const message =
        (error as {data?: {message?: string}})?.data?.message || 'Failed to save settings'
      toast.error(message)
    }
  }, [
    settingsFlow,
    updateFlowSettings,
    stopOnResponse,
    allowMultipleBitesNotifications,
    businessHours,
    refetch,
    refetchFlowStats,
  ])

  const handleCloseSettings = useCallback(() => {
    setSettingsModalOpen(false)
  }, [])

  const {handleDuplicateFlow, duplicatingId} = useDuplicateFlow({
    flows,
    createFlow,
    isAdmin,
    navigate,
    refetch,
    refetchFlowStats,
    t,
    FlowStatus,
    onCloseMenu: () => setPortalMenuAnchor(null),
  })

  const settingsItems: FlowSettingItem[] = useMemo(
    () => [
      {
        feature: 'Stop on response',
        value: stopOnResponse,
        desc:
          'Ends workflow for a Contact if the Contact responds to a message that is sent from this workflow.',
        learnMore: undefined,
        onToggle: setStopOnResponse,
      },
      {
        feature: 'Allow multiple bites notifications',
        value: allowMultipleBitesNotifications,
        desc:
          'Enables sending multiple notification messages to staff when a contact replies in the inbox. This includes bell notifications, SMS, and email notifications.',
        learnMore: undefined,
        onToggle: setAllowMultipleBitesNotifications,
      },
      {
        feature: 'Business hours',
        value: Boolean(businessHours?.status),
        desc: 'Pause this flow outside the configured days and hours.',
        learnMore: undefined,
        onToggle: (checked: boolean) => {
          setBusinessHours((prev) => {
            const base: FlowBusinessHoursSetting = {
              status: checked,
              startTime: prev?.startTime || '09:00',
              endTime: prev?.endTime || '18:00',
              days: prev?.days && prev.days.length > 0 ? prev.days : [1, 2, 3, 4, 5],
              timezone: prev?.timezone || 'Australia/Melbourne',
            }
            return base
          })
        },
      },
    ],
    [stopOnResponse, allowMultipleBitesNotifications, businessHours]
  )

  const confirmation = useMemo(
    () => (
      <>
        <DeleteConfirmationPopup
          show={open}
          handleClose={() => setOpen(false)}
          Message={t('FLOWLIST.DELETE_CONFIRMATION')}
          confirm={handleDeleteConfirm}
        />
        <ResumeFlowModal
          show={resumeModalOpen}
          onHide={() => setResumeModalOpen(false)}
          flowId={resumeFlowId}
          flowName={resumeFlowName}
          onSuccess={handleResumeSuccess}
        />
      </>
    ),
    [open, handleDeleteConfirm, t, resumeModalOpen, resumeFlowId, resumeFlowName, handleResumeSuccess]
  )

  useEffect(() => {
    if (!isLoading && flows.length === 0 && page > 1) {
      setPage(1)
    }
  }, [flows, isLoading, page])

  useEffect(() => {
    refetch()
  }, [location, refetch])

  if (error) {
    toast.error(t('FLOWLIST.FETCH_ERROR'))
  }

  const handleEdit = useCallback(
    (id: string, isAdminUser: boolean) => {
      const editPath = isAdminUser
        ? ROUTES.TEMPLATE_AUTOMATION_FLOW(id)
        : ROUTES.AUTOMATION_FLOW(id)
      navigate(editPath)
    },
    [navigate]
  )

  const handleResume = useCallback(
    (id: string, name: string) => {
      setResumeFlowId(id)
      setResumeFlowName(name)
      setResumeModalOpen(true)
    },
    []
  )

  const handleDelete = useCallback(
    (id: string) => {
      setSelectedId(id)
      setOpen(true)
    },
    []
  )

  // Build menu items for portal menu
  const portalMenuItems = useMemo(() => {
    if (!portalFlow) return []

    return buildMenuItems({
      portalFlow,
      isAdmin,
      duplicatingId,
      FlowStatus,
      t,
      onViewActivity: handleNavigateToInstanceLogs,
      onEdit: handleEdit,
      onSettings: handleOpenSettings,
      onDuplicate: handleDuplicateFlow,
      onToggleStatus: handleToggleStatus,
      onResume: handleResume,
      onDelete: handleDelete,
    })
  }, [
    portalFlow,
    isAdmin,
    duplicatingId,
    FlowStatus,
    t,
    handleNavigateToInstanceLogs,
    handleEdit,
    handleDuplicateFlow,
    handleToggleStatus,
    handleResume,
    handleDelete,
  ])

  return (
    <>
      {confirmation}
      {portalMenuAnchor && portalFlow && (
        <ActionMenuPortal
          anchorEl={portalMenuAnchor}
          onClose={() => setPortalMenuAnchor(null)}
          items={portalMenuItems}
        />
      )}
      <FlowSettingsModal
        show={settingsModalOpen}
        onHide={handleCloseSettings}
        onSave={handleSaveSettings}
        flowName={settingsFlow?.name}
        settings={settingsItems}
        saving={isSavingSettings}
        businessHours={businessHours}
        onBusinessHoursChange={(next) => setBusinessHours(next)}
      />
      <div className={`card mt-8 mb-0`} id='flow-list-section'>
        <div className={`card-header border-0 pt-12 ${isMobile ? 'px-4' : 'px-14'}`}>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-2 mb-1'>{t('FLOWLIST.LIST_FLOW_TITLE')}</span>
            <span className='text-muted mt-1 fw-bold fs-7'>{t('FLOWLIST.LIST_FLOW_SUBTITLE')}</span>
          </h3>

          <div className={`d-flex align-items-center gap-4 ${isMobile ? 'flex-column flex-md-row' : ''}`}>
            {/* Stats bar */}
            <div className='d-none d-md-flex align-items-center gap-4 me-4'>
              <div className='text-center'>
                <div className='text-muted fs-7'>Total</div>
                <div className='fw-bold fs-5'>
                  {isFlowStatsLoading ? '—' : flowStats?.total ?? 0}
                </div>
              </div>
              <div className='vr' />
              <div className='text-center'>
                <div className='text-muted fs-7'>Active</div>
                <div className='fw-bold fs-5 text-success'>
                  {isFlowStatsLoading ? '—' : flowStats?.active ?? 0}
                </div>
              </div>
              <div className='vr' />
              <div className='text-center'>
                <div className='text-muted fs-7'>Inactive</div>
                <div className='fw-bold fs-5 text-muted'>
                  {isFlowStatsLoading ? '—' : flowStats?.inactive ?? 0}
                </div>
              </div>
            </div>

            <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
              <button
                className={`btn btn-primary ${isMobile ? 'flex-fill' : ''}`}
                type='button'
                id='createNewFlow'
                onClick={handleCreateNewFlow}
              >
                <i className='bi bi-plus-lg'></i>
                {t('FLOWLIST.CREATE_NEW_FLOW')}
              </button>
              {flows.length > 0 && !isLoading && (
                <button
                  className={`btn btn-primary ${isMobile ? 'flex-fill' : ''}`}
                  type='button'
                  id='viewLogs'
                  onClick={handleViewLogsPage}
                >
                  <i className='bi bi-eye'></i>
                  {t('FLOWLIST.VIEW_LOGS')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={`card-body py-3 ${isMobile ? 'px-4' : 'px-14'}`}>
          {isMobile ? (
            /* Mobile view: Card layout */
            <div className='mobile-flows-list'>
              {isLoading ? (
                <div className='text-center py-5'>
                  <Spinner animation='border' />
                </div>
              ) : flows.length === 0 ? (
                <div className='text-center py-5 text-muted'>No flows found</div>
              ) : (
                flows.map((flow: Flow) => (
                  <FlowMobileCard
                    key={flow._id}
                    flow={flow}
                    isAdmin={isAdmin}
                    navigate={navigate}
                    handleNavigateToInstanceLogs={handleNavigateToInstanceLogs}
                    handleNavigateToReport={handleNavigateToReport}
                    handleToggleStatus={handleToggleStatus}
                    setSelectedId={setSelectedId}
                    setOpen={setOpen}
                    setResumeModalOpen={setResumeModalOpen}
                    setResumeFlowId={setResumeFlowId}
                    setResumeFlowName={setResumeFlowName}
                    isUpdating={isUpdating}
                    updatingId={updatingId}
                    formatDate={formatDate}
                    FlowStatus={FlowStatus}
                    t={t}
                    handleDuplicateFlow={handleDuplicateFlow}
                    handleOpenSettings={handleOpenSettings}
                  />
                ))
              )}
            </div>
          ) : (
            /* Desktop view: Table layout */
            <div className='table-responsive'>
              <table className='table align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-400 text-muted'>
                    <th className='min-w-120px'>Title</th>
                    <th className='min-w-125px'>Status</th>
                    <th className='min-w-100px'>Bites</th>
                    <th className='min-w-150px'>Contacts</th>
                    <th className='min-w-75px'>Errors</th>
                    <th className='min-w-100px'>Time Created</th>
                    <th className='min-w-100px'>Last Edited</th>
                    <th className='min-w-150px text-end'>{t('FLOWLIST.COLUMN_ACTIONS')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={9} className='text-center'>
                        <Spinner animation='border' />
                      </td>
                    </tr>
                  ) : (
                    flows.map((data: Flow, idx: number) => (
                      <FlowRow
                        isAdmin={isAdmin}
                        key={data._id}
                        data={data}
                        idx={idx}
                        navigate={navigate}
                        handleNavigateToInstanceLogs={handleNavigateToInstanceLogs}
                        handleNavigateToReport={handleNavigateToReport}
                        setSelectedId={setSelectedId}
                        setOpen={setOpen}
                        isUpdating={isUpdating}
                        updatingId={updatingId}
                        handleToggleStatus={handleToggleStatus}
                        t={t}
                        formatDate={formatDate}
                        FlowStatus={FlowStatus}
                        setResumeModalOpen={setResumeModalOpen}
                        setResumeFlowId={setResumeFlowId}
                        setResumeFlowName={setResumeFlowName}
                        handleDuplicateFlow={handleDuplicateFlow}
                        setPortalMenuAnchor={setPortalMenuAnchor}
                        setPortalFlow={setPortalFlow}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {flows.length > 0 && (
          <div className={`abcd ${isMobile ? 'px-4' : 'px-8'} mb-2`}>
            <select
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              value={pageSize}
            >
              {PAGINATION_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ReactPaginate
              previousLabel={PAGINATION_CLASSES.PREVIOUS_LABEL}
              nextLabel={PAGINATION_CLASSES.NEXT_LABEL}
              pageCount={totalPages}
              forcePage={page - 1}
              onPageChange={(e) => setPage(e.selected + 1)}
              containerClassName={PAGINATION_CLASSES.CONTAINER}
              previousLinkClassName={PAGINATION_CLASSES.PREVIOUS_LINK}
              nextLinkClassName={PAGINATION_CLASSES.NEXT_LINK}
              disabledClassName={PAGINATION_CLASSES.DISABLED}
              activeClassName={PAGINATION_CLASSES.ACTIVE}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default FlowList
